import React, { useState, useEffect } from "react";
import payrollService from "../../services/payrollService";
import ApprovalActions from "../../components/Payroll/ApprovalActions";
import PayrollFilters from "../../components/Payroll/PayrollFilters";
import Notification from "../../components/UI/Notification";
import ReactPaginate from "react-paginate";
import { Eye, CheckSquare } from "lucide-react";

const PendingApprovals = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    per_page: 20,
  });
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  useEffect(() => {
    fetchApprovals();
  }, [filters, search, pagination.current_page]);

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const response = await payrollService.getPendingApprovals({
        ...filters,
        page: pagination.current_page,
        search,
      });
      console.log("Fetched approvals:", response);
      if (response.success) {
        setApprovals(response.data.data || []);
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          total: response.data.total,
        });
      }
    } catch (error) {
      console.error("Error fetching approvals:", error);
      setNotification({
        type: "error",
        message: "Failed to fetch pending approvals",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id, payload) => {
    try {
      const response = await payrollService.approvePayroll(id, payload);
      if (response.success) {
        setNotification({
          type: "success",
          message: "Payroll approved successfully",
        });
        fetchApprovals();
      }
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Failed to approve payroll",
      });
    }
  };

  const handleReject = async (id, payload) => {
    try {
      const response = await payrollService.rejectPayroll(id, payload);
      if (response.success) {
        setNotification({
          type: "success",
          message: "Payroll rejected successfully",
        });
        fetchApprovals();
      }
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Failed to reject payroll",
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) {
      setNotification({
        type: "warning",
        message: "Please select at least one item to approve",
      });
      return;
    }

    const remarks = window.prompt(
      "Enter approval remarks for all selected items:"
    );
    if (!remarks) return;

    try {
      await Promise.all(
        selectedItems.map((id) =>
          payrollService.approvePayroll(id, { remarks })
        )
      );
      setNotification({
        type: "success",
        message: `Successfully approved ${selectedItems.length} records`,
      });
      setSelectedItems([]);
      fetchApprovals();
    } catch (error) {
      setNotification({
        type: "error",
        message: "Some records failed to approve",
      });
    }
  };

  const toggleSelection = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === approvals.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(approvals.map((a) => a.id));
    }
  };

  const handlePageChange = ({ selected }) => {
    setPagination((prev) => ({ ...prev, current_page: selected + 1 }));
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      processed:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      approved:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    };
    return colors[status] || colors.draft;
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Pending Approvals
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Review and approve payroll records
          </p>
        </div>
        {selectedItems.length > 0 && (
          <button
            onClick={handleBulkApprove}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
          >
            <CheckSquare size={18} />
            Bulk Approve ({selectedItems.length})
          </button>
        )}
      </div>

      {/* Filters */}
      <PayrollFilters
        filters={filters}
        onFilterChange={(key, value) =>
          setFilters((prev) => ({ ...prev, [key]: value }))
        }
        onSearch={setSearch}
        searchValue={search}
        searchPlaceholder="Search by employee ID or name..."
        showStatus={true}
        statusOptions={[
          { value: "processed", label: "Processed" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
        ]}
      />

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={
                      selectedItems.length === approvals.length &&
                      approvals.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Gross Salary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Net Salary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : approvals.length > 0 ? (
                approvals.map((approval) => (
                  <tr
                    key={approval.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(approval.id)}
                        onChange={() => toggleSelection(approval.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {approval.employee?.full_name || approval.user?.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {approval.employee_id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {approval.payroll_year}-
                      {String(approval.payroll_month).padStart(2, "0")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      ₹
                      {parseFloat(approval.gross_salary).toLocaleString(
                        "en-IN"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 dark:text-green-400">
                      ₹{parseFloat(approval.net_salary).toLocaleString("en-IN")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                          approval.status
                        )}`}
                      >
                        {approval.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {approval.status === "processed" && (
                          <ApprovalActions
                            payrollId={approval.id}
                            onApprove={handleApprove}
                            onReject={handleReject}
                          />
                        )}
                        <button
                          className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    No pending approvals found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              pageCount={pagination.last_page}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={handlePageChange}
              containerClassName="flex items-center justify-center gap-2"
              pageClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              pageLinkClassName="text-gray-700 dark:text-gray-300"
              previousClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              previousLinkClassName="text-gray-700 dark:text-gray-300"
              nextClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              nextLinkClassName="text-gray-700 dark:text-gray-300"
              activeClassName="bg-blue-600 text-white hover:bg-blue-700"
              disabledClassName="opacity-50 cursor-not-allowed"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovals;
